import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { IconInfoTriangle } from '@tabler/icons-react';
import { Suspense } from 'react';

import { DashboardGrid } from '#core/components/dashboard/DashboardGrid.tsx';
import { BoxSkeleton } from '#core/components/dashboard/panels/shared/BoxSkeleton.tsx';
import type { DashboardGridItem } from '#core/components/dashboard/reconciler/serialize.ts';
import { getComponentSkeleton } from '#core/components/dashboard/registry.ts';

import { extractDashboardData } from './helpers.ts';
import type { ShowDashboardInput, ShowDashboardOutput } from './schema.ts';
import { AdaptedGridComponent } from '#plugin-chat/components/dashboard/AdaptedGridComponent.tsx';
import type { ChatDashboardItem } from '#plugin-chat/components/dashboard/types.ts';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';

/**
 * Custom content renderer for DashboardGrid.
 * Returns content to be rendered inside the grid cell wrapper.
 *
 * DashboardGrid handles: positioning, caption, layer styling, error boundary
 * This function handles: data adapter integration
 *
 * Note: index and totalItems are provided by DashboardGrid for streaming detection,
 * but we handle streaming at the data layer (filtering incomplete items) so these
 * params are unused here.
 *
 * @param item - Grid item data from schema
 */
function renderDashboardItemContent(item: DashboardGridItem) {
  const componentType = item.component?.type;
  if (!componentType || typeof componentType !== 'string') {
    return null;
  }

  // Get component-specific skeleton, fall back to generic BoxSkeleton
  const ComponentSkeleton = getComponentSkeleton(componentType) ?? BoxSkeleton;

  return (
    <Suspense fallback={<ComponentSkeleton />}>
      <AdaptedGridComponent item={item as ChatDashboardItem} />
    </Suspense>
  );
}

export function RenderShowDashboardTool({ args }: ToolCallMessagePartProps<ShowDashboardInput, ShowDashboardOutput>) {
  return (
    <ToolDisplay>
      <ToolDisplay.Content shimmer>
        <DashboardGrid data={extractDashboardData(args)} renderItemContent={renderDashboardItemContent} />
      </ToolDisplay.Content>
      <ToolDisplay.Error color="var(--mantine-color-orange-4)" icon={IconInfoTriangle}>
        An error occurred while displaying the dashboard.
      </ToolDisplay.Error>
    </ToolDisplay>
  );
}
